(() => {
  const SELF_SOURCE = 'pt-cart-extras';
  const OPT_OUT_KEY = 'aov_gift_removed';
  const GIFT_PROPERTY = '__aov_gift';
  const GIFT_TIER_PROPERTY = '__aov_gift_tier';

  class PtCartExtras extends HTMLElement {
    constructor() {
      super();
      this.reconcileTimer = null;
      this.reconcilePromise = null;
      this.reconcileDirty = false;
      this.giftBlocked = false;
      this.reconcileAfterImageToken = 0;
      this.upsellIndex = 0;
      this.pendingAddedVariantId = null;
      this.pendingProductImage = null;
      this.pendingProductImageUrl = '';
      this.warmedImages = new Map();
      this.fontWarmPromise = null;
      this.headerCartTemplate = '';
      this.headerCartSvg = '';
      this.drawerHydrationFrame = 0;
      this.assetTimer = null;
      this.nonCriticalReady = false;
      this.boundClick = this.onDocumentClick.bind(this);
      this.boundCaptureClick = this.onDocumentCaptureClick.bind(this);
      this.boundChange = this.onDocumentChange.bind(this);
      this.boundSubmit = this.onDocumentSubmit.bind(this);
      this.boundVisibilityChange = this.syncStripState.bind(this);
    }

    connectedCallback() {
      this.giftVariantId = Number(this.dataset.giftVariantId);
      this.giftGoal = Number(this.dataset.giftGoal);
      this.euroVariantIds = new Set((this.dataset.euroVariantIds || '').split(',').filter(Boolean).map(Number));
      document.addEventListener('click', this.boundCaptureClick, true);
      document.addEventListener('click', this.boundClick);
      document.addEventListener('change', this.boundChange, true);
      document.addEventListener('submit', this.boundSubmit, true);
      document.addEventListener('visibilitychange', this.boundVisibilityChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'product-form') {
            this.pendingAddedVariantId = Number(event.productVariantId) || null;
            return;
          }
          if (event?.source !== SELF_SOURCE) this.scheduleReconcile(80);
        });
      }

      this.prepareCartTrigger();
      this.wrapDrawerOpen();
      this.queueNonCriticalSetup();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundCaptureClick, true);
      document.removeEventListener('click', this.boundClick);
      document.removeEventListener('change', this.boundChange, true);
      document.removeEventListener('submit', this.boundSubmit, true);
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      this.cartUpdateUnsubscriber?.();
      this.headerCartObserver?.disconnect();
      this.drawerMutationObserver?.disconnect();
      cancelAnimationFrame(this.drawerHydrationFrame);
      clearTimeout(this.reconcileTimer);
      if ('cancelIdleCallback' in window) cancelIdleCallback(this.assetTimer);
      else clearTimeout(this.assetTimer);
      this.assetTimer = null;
      this.nonCriticalReady = false;
    }

    wrapDrawerOpen() {
      customElements.whenDefined('cart-drawer').then(() => {
        const DrawerClass = customElements.get('cart-drawer');
        if (!DrawerClass || DrawerClass.prototype.ptRewardWrapped) return;
        const originalOpen = DrawerClass.prototype.open;
        DrawerClass.prototype.open = function (...args) {
          const extras = document.querySelector('pt-cart-extras');
          extras?.initializeNonCritical();
          extras?.warmDrawerFont();
          const priorityImage = extras?.hydrateVisibleCartImages(this, this.productId);
          extras?.hydrateNonCriticalImages(this);
          const result = originalOpen.apply(this, args);
          extras?.updateCartCountFromDrawer();
          setTimeout(() => extras?.syncStripState(), 50);
          extras?.scheduleReconcileAfterImage(priorityImage);
          return result;
        };
        DrawerClass.prototype.ptRewardWrapped = true;
      });
    }

    prepareCartTrigger() {
      const trigger = document.querySelector('[data-ai-header-cart-link]') || document.querySelector('#cart-icon-bubble');
      if (!trigger) return null;
      if (!trigger.id) trigger.id = 'cart-icon-bubble';
      return trigger;
    }

    queueNonCriticalSetup() {
      const run = () => {
        this.assetTimer = null;
        this.initializeNonCritical();
      };
      if ('requestIdleCallback' in window) {
        this.assetTimer = requestIdleCallback(run, { timeout: 1200 });
      } else {
        this.assetTimer = setTimeout(run, 180);
      }
    }

    initializeNonCritical() {
      if (this.nonCriticalReady || !this.isConnected) return;
      this.nonCriticalReady = true;
      if (this.assetTimer !== null) {
        if ('cancelIdleCallback' in window) cancelIdleCallback(this.assetTimer);
        else clearTimeout(this.assetTimer);
        this.assetTimer = null;
      }
      this.ensureCartTrigger();
      this.observeDrawerMutations();
      this.updateCartCountFromDrawer();
      this.syncStripState();
      this.loadComplementaryRecommendations();
      if (this.euroVariantIds.size) this.scheduleReconcile(0);
    }

    async loadComplementaryRecommendations() {
      const host = this.closest('cart-drawer')?.querySelector('[data-pt-cart-recommendations]');
      if (!host?.dataset.productId || host.dataset.loaded) return;
      host.dataset.loaded = 'true';
      const url = new URL(host.dataset.url, window.location.origin);
      url.searchParams.set('product_id', host.dataset.productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('section_id', 'pt-cart-recommendations');
      try {
        for (const intent of ['complementary', 'related']) {
        url.searchParams.set('intent', intent);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const carousel = html.querySelector('[data-pt-native-complements] [data-pt-upsell-carousel]');
        if (!host.isConnected) return;
        if (!carousel) continue;
        // Explicit complements, then Shopify's related products, then the merchant's collection.
        host.replaceChildren(carousel);
        this.showUpsell(0);
        this.hydrateNonCriticalImages(this.closest('cart-drawer'));
        return;
        }
      } catch (_error) { /* The merchant's collection remains available when no complements exist. */ }
    }

    ensureCartTrigger() {
      const trigger = this.prepareCartTrigger();
      if (!trigger) return;

      if (trigger.matches('[data-ai-header-cart-link]')) {
        this.headerCartTemplate ||= trigger.innerHTML;
        this.headerCartSvg ||= trigger.querySelector('svg')?.outerHTML || '';
        this.headerCartObserver?.disconnect();
        this.headerCartObserver = new MutationObserver(() => this.restoreHeaderCartIcon(trigger));
        this.headerCartObserver.observe(trigger, { childList: true, subtree: true });
      }

      customElements.whenDefined('cart-drawer').then(() => {
        const drawer = this.closest('cart-drawer');
        if (drawer && trigger.getAttribute('role') !== 'button') drawer.setHeaderCartIconAccessibility?.();
      });
    }

    restoreHeaderCartIcon(trigger = document.querySelector('[data-ai-header-cart-link]')) {
      if (!trigger || !this.headerCartTemplate || !this.headerCartSvg) return;
      if (trigger.querySelector('svg')?.outerHTML === this.headerCartSvg && trigger.querySelector('[data-ai-header-cart-count]')) return;
      trigger.innerHTML = this.headerCartTemplate;
      this.updateCartCountFromDrawer();
    }

    observeDrawerMutations() {
      const drawer = this.closest('cart-drawer');
      if (!drawer) return;
      this.drawerMutationObserver?.disconnect();
      this.drawerMutationObserver = new MutationObserver((records) => {
        if (!drawer.classList.contains('active')) return;
        const hasPendingImage = records.some((record) => [...record.addedNodes].some((node) =>
          node.nodeType === Node.ELEMENT_NODE && (node.matches?.('img[data-pt-src]') || node.querySelector?.('img[data-pt-src]')),
        ));
        if (!hasPendingImage) return;
        cancelAnimationFrame(this.drawerHydrationFrame);
        this.drawerHydrationFrame = requestAnimationFrame(() => {
          if (!drawer.isConnected || !drawer.classList.contains('active')) return;
          this.hydrateVisibleCartImages(drawer, this.pendingAddedVariantId);
          this.hydrateNonCriticalImages(drawer);
        });
      });
      this.drawerMutationObserver.observe(drawer, { childList: true, subtree: true });
    }

    hydrateImage(image, priority = 'auto') {
      if (!image) return Promise.resolve();
      const source = image.dataset.ptSrc;
      if (!source) return Promise.resolve();
      if (image.ptHydrationPromise) return image.ptHydrationPromise;

      image.ptHydrationPromise = new Promise((resolve) => {
        const finish = () => {
          const loaded = image.complete && image.naturalWidth > 0;
          image.classList.remove('pt-cart-image--loading');
          image.classList.toggle('pt-cart-image--error', !loaded);
          if (loaded) {
            image.removeAttribute('data-pt-src');
            image.removeAttribute('data-pt-srcset');
          }
          delete image.ptHydrationPromise;
          resolve(image);
        };
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
      });

      image.classList.remove('pt-cart-image--error');
      image.classList.add('pt-cart-image--loading');
      image.loading = priority === 'high' ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.fetchPriority = priority;
      if (image.dataset.ptSrcset) image.srcset = image.dataset.ptSrcset;
      image.src = source;
      return image.ptHydrationPromise;
    }

    hydrateVisibleCartImages(drawer = this.closest('cart-drawer'), variantId = this.pendingAddedVariantId) {
      if (!drawer) return Promise.resolve();
      const images = [...drawer.querySelectorAll('.pt-cart-item__image[data-pt-src]')];
      if (!images.length) return Promise.resolve();
      const priorityImage = images.find((image) => Number(image.dataset.ptVariantId) === Number(variantId)) ||
        images.find((image) => !image.closest('.pt-cart-item--gift')) || images[0];
      const priorityPromise = this.hydrateImage(priorityImage, 'high');
      images.filter((image) => image !== priorityImage).forEach((image) => this.hydrateImage(image, 'auto'));
      return priorityPromise;
    }

    hydrateNonCriticalImages(drawer = this.closest('cart-drawer')) {
      drawer?.querySelectorAll('img[data-pt-src]:not(.pt-cart-item__image)').forEach((image) => {
        this.hydrateImage(image, 'low');
      });
    }

    warmDrawerImages(drawer = this.closest('cart-drawer')) {
      drawer?.querySelectorAll('img[data-pt-src]').forEach((image) => {
        const source = image.dataset.ptSrc;
        if (!source || this.warmedImages.has(source)) return;
        const preloader = new Image();
        preloader.decoding = 'async';
        preloader.fetchPriority = image.classList.contains('pt-cart-item__image') ? 'high' : 'low';
        preloader.src = source;
        this.warmedImages.set(source, preloader);
      });
    }

    warmDrawerFont() {
      if (!document.fonts?.load) return Promise.resolve();
      this.fontWarmPromise ||= document.fonts.load('400 1em "PT Poppins"').catch(() => []);
      return this.fontWarmPromise;
    }

    belongsToDrawer(target) {
      return target?.closest?.('cart-drawer') === this.closest('cart-drawer');
    }

    onDocumentCaptureClick(event) {
      const productSubmit = event.target.closest?.('product-form [type="submit"]');
      const quickAddSubmit = event.target.closest?.('[class*="ai-product-grid__product-form-"] form [type="submit"]');
      const cartTrigger = event.target.closest?.('#cart-icon-bubble, [data-ai-header-cart-link]');
      if (productSubmit || quickAddSubmit) {
        this.initializeNonCritical();
        this.warmDrawerFont();
        this.preloadProductImage((productSubmit || quickAddSubmit).closest('form'));
        this.warmDrawerImages();
      } else if (cartTrigger) {
        this.initializeNonCritical();
        this.warmDrawerFont();
        this.warmDrawerImages();
      }

      const giftRemoveButton = event.target.closest?.('[data-pt-reward-gift] cart-remove-button button');
      if (giftRemoveButton && this.belongsToDrawer(giftRemoveButton)) {
        this.addGiftOptOut();
        return;
      }

      const groupedRemoveButton = event.target.closest?.('.pt-cart-item:not([data-pt-reward-gift]) cart-remove-button button[data-pt-line-keys]');
      const pageKitRemove = event.target.closest?.('#main-cart-items [data-bm-cart-kit-remove]');
      if ((groupedRemoveButton && this.belongsToDrawer(groupedRemoveButton)) || pageKitRemove) {
        event.preventDefault();
        event.stopPropagation();
        this.updateGroupedQuantity(pageKitRemove || groupedRemoveButton, 0);
      }
    }

    onDocumentClick(event) {
      const closeLink = event.target.closest?.('[data-pt-cart-close]');
      if (closeLink && this.belongsToDrawer(closeLink)) {
        this.closest('cart-drawer')?.close();
        return;
      }

      const dot = event.target.closest?.('[data-pt-upsell-dot]');
      if (dot && this.belongsToDrawer(dot)) {
        event.preventDefault();
        this.showUpsell(Number(dot.dataset.ptUpsellDot), true);
        return;
      }

      const addButton = event.target.closest?.('[data-pt-upsell-add]');
      if (addButton && this.belongsToDrawer(addButton)) {
        event.preventDefault();
        this.addUpsell(addButton);
      }
    }

    onDocumentChange(event) {
      const quantityInput = event.target.closest?.('.pt-cart-quantity .quantity__input[data-pt-line-keys]');
      if (quantityInput && this.belongsToDrawer(quantityInput)) {
        event.preventDefault();
        event.stopPropagation();
        this.updateGroupedQuantity(quantityInput, Number(quantityInput.value));
        return;
      }

      const select = event.target.closest?.('[data-pt-upsell-variant]');
      if (!select || !this.belongsToDrawer(select)) return;
      const card = select.closest('[data-pt-upsell-slide]');
      const option = select.selectedOptions[0];
      const button = card?.querySelector('[data-pt-upsell-add]');
      const price = card?.querySelector('[data-pt-upsell-price]');
      if (button) button.dataset.variantId = select.value;
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
    }

    onDocumentSubmit(event) {
      const submittedForm = event.target;
      if (this.isQuickAddForm(submittedForm)) {
        this.initializeNonCritical();
        event.preventDefault();
        this.addQuickAddForm(submittedForm);
        return;
      }

      const productForm = submittedForm.closest?.('product-form form');
      if (productForm) {
        this.initializeNonCritical();
        this.preloadProductImage(productForm);
      }
    }

    isQuickAddForm(form) {
      if (!(form instanceof HTMLFormElement) || form.closest('product-form')) return false;
      if (!form.closest('[class*="ai-product-grid__product-form-"]')) return false;

      try {
        const path = new URL(form.action, window.location.href).pathname.replace(/\.js$/, '');
        return path.endsWith('/cart/add');
      } catch (_error) {
        return false;
      }
    }

    preloadProductImage(form) {
      const productInfo = form?.closest('product-info');
      const section = productInfo?.closest('.shopify-section') ||
        form?.closest('article, [class*="ai-product-grid__item-"]') ||
        productInfo?.parentElement || document;
      const image = productInfo
        ? section.querySelector('media-gallery .product__media-item.is-active img[src], media-gallery .is-active img[src], media-gallery img[src]')
        : section.querySelector('img[src*="/cdn/shop/"]');
      const source = image?.currentSrc || image?.src;
      if (!source) return;

      try {
        const url = new URL(source, window.location.href);
        if (!url.pathname.includes('/cdn/shop/')) return;
        url.searchParams.set('width', '204');
        const preloadUrl = url.toString();
        if (preloadUrl === this.pendingProductImageUrl) return;
        const preloader = new Image();
        preloader.decoding = 'async';
        preloader.fetchPriority = 'high';
        preloader.src = preloadUrl;
        this.pendingProductImage = preloader;
        this.pendingProductImageUrl = preloadUrl;
      } catch (_error) {
        // A malformed media URL should never block adding an item to the cart.
      }
    }

    syncStripState() {
      const strip = this.closest('cart-drawer')?.querySelector('[data-pt-cart-rotating-strip]');
      if (!strip) return;
      strip.classList.toggle('is-page-hidden', document.hidden);
    }

    showUpsell(index, announce = false) {
      const drawer = this.closest('cart-drawer');
      const slides = [...(drawer?.querySelectorAll('[data-pt-upsell-slide]') || [])];
      if (!slides.length) return;
      const normalizedIndex = ((index % slides.length) + slides.length) % slides.length;
      this.upsellIndex = normalizedIndex;
      slides.forEach((slide) => {
        const active = Number(slide.dataset.ptUpsellSlide) === normalizedIndex;
        slide.classList.toggle('is-active', active);
      });
      drawer?.querySelectorAll('[data-pt-upsell-dot]').forEach((dot) => {
        const active = Number(dot.dataset.ptUpsellDot) === normalizedIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
        dot.toggleAttribute('aria-current', active);
      });
      if (announce) {
        const activeDot = drawer?.querySelector(`[data-pt-upsell-dot="${normalizedIndex}"]`);
        const status = drawer?.querySelector('[data-pt-upsell-status]');
        if (status) status.textContent = activeDot?.getAttribute('aria-label') || '';
      }
    }

    scheduleReconcileAfterImage(imagePromise) {
      const token = ++this.reconcileAfterImageToken;
      const timeout = new Promise((resolve) => setTimeout(resolve, 700));
      Promise.race([imagePromise || Promise.resolve(), timeout]).then(() => {
        if (token === this.reconcileAfterImageToken) this.scheduleReconcile(0);
      });
    }

    async addQuickAddForm(form) {
      const button = form.querySelector('[type="submit"]');
      if (!button || button.disabled || form.dataset.ptSubmitting === 'true') return;

      form.dataset.ptSubmitting = 'true';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('pt-quick-add--loading');
      this.clearError();

      const formData = new FormData(form);
      const variantId = Number(formData.get('id')) || null;
      formData.set('sections', this.getSectionIds().join(','));
      formData.set('sections_url', window.location.pathname);

      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(routes.cart_add_url, config);
        const payload = await response.json();
        if (!response.ok || payload.errors || payload.status) {
          throw new Error(payload.description || payload.message || this.dataset.addErrorMessage || '');
        }

        this.pendingAddedVariantId = variantId;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: payload,
          });
        }

        const drawer = this.closest('cart-drawer');
        drawer?.setActiveElement(button);
        drawer?.classList.remove('is-empty');
        await drawer?.animateAddition?.(button);
        drawer?.renderContents(payload);
      } catch (error) {
        this.showError(error.message || this.dataset.addErrorMessage);
      } finally {
        delete form.dataset.ptSubmitting;
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
        button.classList.remove('pt-quick-add--loading');
      }
    }

    async addUpsell(button) {
      if (button.disabled) return;
      button.disabled = true;
      this.clearError();

      try {
        const payload = await this.cartRequest(routes.cart_add_url, {
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
        }, this.dataset.addErrorMessage);
        this.pendingAddedVariantId = Number(button.dataset.variantId);
        const priorityImage = this.renderSections(payload.sections);
        const drawer = this.closest('cart-drawer');
        drawer?.classList.remove('is-empty');
        drawer?.open(document.querySelector('#cart-icon-bubble'));
        this.scheduleReconcileAfterImage(priorityImage);
      } catch (error) {
        this.showError(this.dataset.addErrorMessage || error.message);
        button.disabled = false;
      }
    }

    async updateGroupedQuantity(control, requestedQuantity) {
      const row = control.closest('.pt-cart-item, [data-bm-cart-kit]');
      const input = control.matches('.quantity__input') ? control : row?.querySelector('.quantity__input');
      const rawKeys = control.dataset.ptLineKeys || input?.dataset.ptLineKeys || '';
      const lineKeys = rawKeys.split(',').map((key) => key.trim()).filter(Boolean);
      const quantity = Math.max(0, Number(requestedQuantity) || 0);
      if (!row || !lineKeys.length || row.dataset.ptUpdating === 'true') return;

      row.dataset.ptUpdating = 'true';
      row.querySelectorAll('button, input, select').forEach((element) => { element.disabled = true; });
      row.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.clearError();

      const groupId = row.dataset.ptLineGroup;
      const focusName = document.activeElement?.getAttribute?.('name');
      const updates = {};
      lineKeys.forEach((key, index) => { updates[key] = index === 0 ? quantity : 0; });

      try {
        const payload = await this.cartRequest(routes.cart_update_url, { updates });
        const priorityImage = this.renderSections(payload.sections, payload);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: SELF_SOURCE, cartData: payload });
        }
        this.scheduleReconcileAfterImage(priorityImage);

        requestAnimationFrame(() => {
          const drawer = this.closest('cart-drawer');
          const updatedRow = groupId ? drawer?.querySelector(`[data-pt-line-group="${groupId}"]`) : null;
          const focusTarget = focusName ? updatedRow?.querySelector(`[name="${focusName}"]`) : null;
          (focusTarget || updatedRow?.querySelector('.quantity__input') || drawer?.querySelector('#CartDrawer-Checkout'))?.focus();
        });
      } catch (error) {
        if (input) input.value = input.getAttribute('value') || '1';
        this.showError(this.dataset.updateErrorMessage || error.message);
        row.querySelectorAll('button, input, select').forEach((element) => { element.disabled = false; });
        row.querySelector('.loading__spinner')?.classList.add('hidden');
        delete row.dataset.ptUpdating;
      }
    }

    scheduleReconcile(delay = 0) {
      clearTimeout(this.reconcileTimer);
      this.reconcileTimer = setTimeout(() => this.reconcileGift(), delay);
    }

    async reconcileGift() {
      if (!this.giftVariantId || !this.giftGoal) return;
      if (this.reconcilePromise) {
        this.reconcileDirty = true;
        return this.reconcilePromise;
      }

      this.reconcilePromise = (PtCartExtras.rewardWork || Promise.resolve()).then(async () => {
        do {
          this.reconcileDirty = false;
          await this.reconcileGiftOnce();
        } while (this.reconcileDirty);
      })
        .catch((error) => this.showError(error.message))
        .finally(() => {
          this.reconcilePromise = null;
        });
      PtCartExtras.rewardWork = this.reconcilePromise;
      return this.reconcilePromise;
    }

    async reconcileGiftOnce() {
      let cart = await this.fetchCart();
      if (await this.reconcileEuroOffer(cart)) cart = await this.fetchCart();
      const managedGifts = cart.items.filter((item) => this.isManagedGift(item));

      if (this.shouldSuppressNormalGiftForCode100(cart)) {
        if (managedGifts.length) await this.updateGiftLines(managedGifts, 0);
        return;
      }

      const merchandiseTotal = cart.items.reduce(
        (total, item) => total + (this.isManagedGift(item) || this.isEuroReward(item) || String(item.properties?.__aov_gift) === 'true' ? 0 : Number(item.final_line_price || 0)),
        0,
      );
      const eligible = merchandiseTotal >= this.giftGoal;
      const optedOut = this.hasGiftOptOut();

      if (!eligible) this.giftBlocked = false;

      if ((!eligible || optedOut) && managedGifts.length) {
        await this.updateGiftLines(managedGifts, 0);
        return;
      }

      if (!eligible || optedOut || this.giftBlocked) return;

      if (!managedGifts.length) {
        const added = await this.cartRequest(routes.cart_add_url, {
          items: [
            {
              id: this.giftVariantId,
              quantity: 1,
              properties: { [GIFT_PROPERTY]: true, [GIFT_TIER_PROPERTY]: 1 },
            },
          ],
        });

        const verifiedCart = await this.fetchCart();
        const verifiedGifts = verifiedCart.items.filter((item) => this.isManagedGift(item));
        if (!verifiedGifts.length || verifiedGifts.some((item) => !this.isFreeGift(item))) {
          this.giftBlocked = true;
          if (verifiedGifts.length) await this.updateGiftLines(verifiedGifts, 0);
          throw new Error(this.dataset.errorMessage || '');
        }

        this.renderSections(added.sections, verifiedCart);
        return;
      }

      const invalidGift = managedGifts.some((item) => !this.isFreeGift(item));
      if (invalidGift) {
        this.giftBlocked = true;
        await this.updateGiftLines(managedGifts, 0);
        throw new Error(this.dataset.errorMessage || '');
      }

      const needsNormalization = managedGifts.length > 1 || managedGifts[0].quantity !== 1;
      if (needsNormalization) await this.updateGiftLines(managedGifts, 1);
    }

    isEuroReward(item) {
      return this.euroVariantIds?.has(Number(item.variant_id || item.id)) || String(item.properties?.__pt_offer1) === 'true';
    }

    async reconcileEuroOffer(cart) {
      const rewards = cart.items.filter((item) => this.isEuroReward(item));
      if (!rewards.length) return false;
      const merchandise = cart.items.reduce((sum, item) => sum + (this.isEuroReward(item) || String(item.properties?.__aov_gift) === 'true' || this.isManagedGift(item) ? 0 : Number(item.final_line_price || 0)), 0);
      const eligible = cart.currency === 'EUR' && merchandise >= 8000;
      let retained = false;
      const updates = {};
      for (const item of rewards) {
        const valid = eligible && !retained && this.euroVariantIds?.has(Number(item.variant_id || item.id)) && Number(item.original_price) === 100 && Number(item.final_line_price) === 100 * Number(item.quantity);
        const quantity = valid ? 1 : 0;
        if (valid) retained = true;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      }
      if (!Object.keys(updates).length) return false;
      const payload = await this.cartRequest(routes.cart_update_url, { updates });
      this.renderSections(payload.sections, payload);
      return true;
    }

    shouldSuppressNormalGiftForCode100(cart) {
      const codeApplied = (cart.cart_level_discount_applications || []).some((discount) => {
        const type = String(discount.type || '').toLowerCase();
        const code = String(discount.code || discount.title || '').trim().toUpperCase();
        return (type === 'discount_code' || type === 'discountcode') && code === '100';
      });
      if (codeApplied) return true;

      try {
        return window.sessionStorage.getItem('promo-code-100-pending') === '100';
      } catch (_error) {
        return false;
      }
    }

    isManagedGift(item) {
      const value = item.properties?.[GIFT_PROPERTY];
      return Number(item.variant_id || item.id) === this.giftVariantId && (value === true || String(value) === 'true');
    }

    isFreeGift(item) {
      const hasDiscount = (item.discounts || []).some((discount) => discount.title === 'Super gift');
      return Number(item.final_line_price) === 0 && hasDiscount;
    }

    async updateGiftLines(gifts, retainedQuantity) {
      const updates = {};
      gifts.forEach((gift, index) => {
        updates[gift.key] = index === 0 ? retainedQuantity : 0;
      });
      const payload = await this.cartRequest(routes.cart_update_url, { updates });
      this.renderSections(payload.sections, payload);
    }

    async fetchCart() {
      const response = await fetch(`${routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      if (!response.ok || cart.errors) throw new Error(this.dataset.cartUnavailableMessage || '');
      this.updateCartCount(cart);
      return cart;
    }

    getSectionIds() {
      const ids = ['cart-drawer'];
      const mainItemsId = document.getElementById('main-cart-items')?.dataset.id;
      const mainFooterId = document.getElementById('main-cart-footer')?.dataset.id;
      if (mainItemsId) ids.push(mainItemsId);
      if (mainFooterId) ids.push(mainFooterId);
      return [...new Set(ids)];
    }

    async cartRequest(url, data, fallbackMessage = this.dataset.updateErrorMessage) {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...data,
          sections: this.getSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload.errors || payload.status) {
        throw new Error(fallbackMessage || this.dataset.updateErrorMessage || '');
      }
      return payload;
    }

    renderSections(sections = {}, cartState = null) {
      const drawer = this.closest('cart-drawer');
      const targetCart = document.getElementById('CartDrawer');
      const drawerHtml = sections['cart-drawer'];
      const previousScroll = drawer?.querySelector('cart-drawer-items')?.scrollTop || 0;
      const euroWasOpen = Boolean(drawer?.querySelector('pt-euro-offer dialog')?.open);
      let priorityImage = Promise.resolve();

      if (drawer && targetCart && drawerHtml) {
        const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const sourceCart = parsed.getElementById('CartDrawer');
        const sourceDrawer = parsed.querySelector('cart-drawer');
        if (sourceCart) {
          targetCart.innerHTML = sourceCart.innerHTML;
          if (euroWasOpen) drawer.querySelector('pt-euro-offer')?.open();
          drawer.classList.toggle('is-empty', sourceDrawer?.classList.contains('is-empty') || Boolean(sourceCart.querySelector('.pt-cart-empty')));
          drawer.querySelector('cart-drawer-items')?.scrollTo({ top: previousScroll });
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
          if (drawer.classList.contains('active')) {
            priorityImage = this.hydrateVisibleCartImages(drawer, this.pendingAddedVariantId);
            this.hydrateNonCriticalImages(drawer);
            this.syncStripState();
          }
        }
      }

      this.loadComplementaryRecommendations();
      this.renderMainCartSection(sections, 'main-cart-items');
      this.renderMainCartSection(sections, 'main-cart-footer');
      if (this.isFullCart(cartState)) this.updateCartCount(cartState);
      else this.updateCartCountFromDrawer();
      return priorityImage;
    }

    isFullCart(cart) {
      return Array.isArray(cart?.items) && Number.isFinite(Number(cart?.item_count));
    }

    getMerchandiseCount(cart) {
      if (!this.isFullCart(cart)) return null;
      return cart.items.reduce(
        (count, item) => count + (this.isManagedGift(item) ? 0 : Math.max(0, Number(item.quantity) || 0)),
        0,
      );
    }

    updateCartCount(cart) {
      const count = this.getMerchandiseCount(cart);
      if (count === null) return;
      this.writeCartCount(count);
    }

    updateCartCountFromDrawer() {
      const drawer = this.closest('cart-drawer');
      if (!drawer) return;
      const inputs = [...drawer.querySelectorAll('.pt-cart-item:not([data-pt-reward-gift]) .quantity__input')];
      const kitCount = [...drawer.querySelectorAll('[data-bm-kit-count]')].reduce((total, row) => total + Number(row.dataset.bmKitCount), 0);
      if (!inputs.length && !kitCount && !drawer.querySelector('.pt-cart-empty')) {
        const initialCount = Number(this.dataset.merchandiseCount);
        if (Number.isFinite(initialCount)) this.writeCartCount(initialCount);
        return;
      }
      const count = inputs.reduce((total, input) => total + Math.max(0, Number(input.value) || 0), kitCount);
      this.writeCartCount(count);
    }

    writeCartCount(count) {
      const trigger = document.querySelector('#cart-icon-bubble, [data-ai-header-cart-link]');
      if (!trigger) return;
      const header = trigger.closest('[data-is-homepage]');
      if (typeof header?.updateCartCount === 'function') {
        header.updateCartCount(count);
        return;
      }

      const counter = trigger.querySelector('[data-ai-header-cart-count]');
      if (counter) {
        counter.textContent = count > 99 ? '99+' : String(count || '');
        counter.toggleAttribute('hidden', count === 0);
      }

      const bubble = trigger.querySelector('.cart-count-bubble');
      if (bubble) {
        const visible = bubble.querySelector('[aria-hidden="true"]') || bubble;
        visible.textContent = String(count);
        bubble.toggleAttribute('hidden', count === 0);
      }

      const baseLabel = this.dataset.cartLabel || trigger.getAttribute('aria-label')?.split(',')[0] || '';
      const countLabel = count === 1
        ? this.dataset.cartCountOne
        : this.dataset.cartCountOther?.replace('[count]', String(count));
      trigger.setAttribute('aria-label', count > 0 && countLabel ? `${baseLabel}, ${countLabel}` : baseLabel);
    }

    renderMainCartSection(sections, elementId) {
      const targetRoot = document.getElementById(elementId);
      const sectionId = targetRoot?.dataset.id;
      if (!targetRoot || !sectionId || !sections[sectionId]) return;
      const parsed = new DOMParser().parseFromString(sections[sectionId], 'text/html');
      const sourceContents = parsed.querySelector(`#${elementId} .js-contents`) || parsed.querySelector('.js-contents');
      const targetContents = targetRoot.querySelector('.js-contents');
      if (sourceContents && targetContents) targetContents.innerHTML = sourceContents.innerHTML;
    }

    hasGiftOptOut() {
      try {
        const value = JSON.parse(localStorage.getItem(OPT_OUT_KEY) || '[]');
        return Array.isArray(value) && value.map(String).includes(String(this.giftVariantId));
      } catch (_error) {
        return false;
      }
    }

    addGiftOptOut() {
      try {
        const value = JSON.parse(localStorage.getItem(OPT_OUT_KEY) || '[]');
        const variants = Array.isArray(value) ? value.map(String) : [];
        if (!variants.includes(String(this.giftVariantId))) variants.push(String(this.giftVariantId));
        localStorage.setItem(OPT_OUT_KEY, JSON.stringify(variants));
      } catch (_error) {
        // Storage can be unavailable in strict privacy modes; cart removal still works.
      }
    }

    clearError() {
      const error = document.getElementById('CartDrawer-CartErrors');
      if (error) error.textContent = '';
    }

    showError(message) {
      const error = document.getElementById('CartDrawer-CartErrors');
      if (error) error.textContent = message || this.dataset.errorMessage || '';
    }
  }

  if (!customElements.get('pt-cart-extras')) customElements.define('pt-cart-extras', PtCartExtras);
})();
